import {
  dropdown,
  multi,
  OTHER,
  option,
  single,
  text,
  type UsFieldDef,
  type UsOption,
} from "../us-fields";

export const PERICARDIAL_FREE_FLUID = single(
  "cardiac.pericardial_effusion",
  "Pericardium",
  [
    option("absent", "No pericardial free fluid"),
    option("present", "Pericardial free fluid"),
    option("indeterminate", "indeterminate"),
  ],
  { normal: "absent" },
);

export const PERICARDIAL_EFFUSION = single(
  "cardiac.pericardial_effusion",
  "Pericardial effusion",
  [
    option("absent", "Absent"),
    option("present", "Present", {
      reveals: [
        single("cardiac.effusion_size", "Size", [
          option("small", "Small (0-1 cm)"),
          option("moderate", "Moderate (1-2 cm)"),
          option("large", "Large (>2 cm)"),
        ]),
        single("cardiac.tamponade", "Cardiac Tamponade", [
          option("absent", "Absent"),
          option("present", "Present, with findings of", {
            reveals: [
              dropdown("cardiac.tamponade_findings", "Tamponade findings", [
                option("not_applicable", "N/A"),
                option("diastolic_rv_collapse", "Diastolic RV Collapse"),
                option("systolic_ra_collapse", "Systolic RA Collapse"),
                option(
                  "mitral_inflow_variation",
                  "Mitral Inflow Respiratory Variation >25%",
                ),
              ]),
            ],
          }),
          option("indeterminate", "Indeterminate"),
        ]),
      ],
    }),
    option("indeterminate", "Indeterminate"),
  ],
  { normal: "absent" },
);

export const cardiacFindings = (
  otherLabel = "Other cardiac findings",
): readonly UsFieldDef[] => [
  PERICARDIAL_EFFUSION,
  single(
    "cardiac.systolic_function",
    "Systolic function",
    [
      option("normal", "Normal"),
      option("mildly_decreased", "Mildly decreased (EF 30-50%)"),
      option("severely_decreased", "Severely decreased (EF<30%)"),
      option("cardiac_standstill", "Cardiac standstill"),
      option("hyperdynamic", "Hyperdynamic"),
      option("indeterminate", "Indeterminate"),
    ],
    { normal: "normal" },
  ),
  single(
    "cardiac.rv_lv_ratio",
    "Right ventricular to left ventricular size",
    [
      option("normal", "Normal (<0.6)"),
      option("ratio_0_6_to_1_0", "0.6-1.0"),
      option("ratio_over_1_0", ">1.0"),
      option("indeterminate", "Indeterminate"),
    ],
    { normal: "normal" },
  ),
  single(
    "cardiac.rv_strain",
    "Right ventricular strain",
    [
      option("absent", "Absent"),
      option("present", "Present, with findings of", {
        reveals: [
          dropdown(
            "cardiac.rv_strain_findings",
            "Right ventricular strain findings",
            [
              option("not_applicable", "N/A"),
              option("septal_bowing", "Interventricular septal bowing"),
              option("mcconnell_sign", "McConnel's Sign"),
              option("tapse", "TAPSE"),
            ],
          ),
        ],
      }),
      option("indeterminate", "Indeterminate"),
    ],
    { normal: "absent" },
  ),
  single(
    "cardiac.ivc_size",
    "Size",
    [
      option("under_1_cm", "<1 cm"),
      option("from_1_to_2_cm", "1-2 cm"),
      option("over_2_cm", ">2 cm"),
    ],
    { heading: "Inferior Vena Cava (IVC)" },
  ),
  single("cardiac.ivc_collapse", "Collapse", [
    option("flat", "flat"),
    option("over_75", ">75%"),
    option("from_25_to_75", "25-75%"),
    option("under_25", "<25%"),
  ]),
  text("cardiac.other_findings", otherLabel),
];

export const CARDIAC_IMPRESSION_OPTIONS = {
  normal: option("normal_cardiac", "Normal cardiac POCUS"),
  effusionWithoutTamponade: option(
    "pericardial_effusion_without_tamponade",
    "Pericardial effusion without tamponade",
  ),
  tamponade: option("pericardial_tamponade", "Pericardial tamponade"),
  systolicDysfunction: option("systolic_dysfunction", "Systolic dysfunction"),
  rvStrain: option("rv_strain", "Right ventricular strain"),
} satisfies Record<string, UsOption>;

export const CARDIAC_IVC_IMPRESSION = single("cardiac.impression_ivc", "IVC", [
  option("fluid_tolerant", "Fluid tolerant/responsive"),
  option("not_fluid_tolerant", "Not fluid tolerant/responsive"),
  option("indeterminate", "indeterminate"),
]);

export const CARDIAC_INDETERMINATE_FOR = [
  option("pericardial_effusion", "Pericardial Effusion"),
  option("pericardial_tamponade", "Pericardial Tamponade"),
  option("systolic_function", "Systolic function"),
  option("rv_size", "RV size"),
  option("rv_strain", "RV strain"),
  option("fluid_responsiveness", "Fluid responsiveness"),
];

export const indeterminateFor = (targets: readonly UsOption[]): UsOption =>
  option("indeterminate_for", "indeterminate for", {
    reveals: [multi("cardiac.indeterminate_for", "indeterminate for", targets)],
  });

export const CARDIAC_IMPRESSION = multi("cardiac.impression", "Impression", [
  CARDIAC_IMPRESSION_OPTIONS.normal,
  CARDIAC_IMPRESSION_OPTIONS.effusionWithoutTamponade,
  CARDIAC_IMPRESSION_OPTIONS.tamponade,
  CARDIAC_IMPRESSION_OPTIONS.systolicDysfunction,
  CARDIAC_IMPRESSION_OPTIONS.rvStrain,
  indeterminateFor(CARDIAC_INDETERMINATE_FOR),
  OTHER,
]);
